import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronRight, 
  Star, 
  Users, 
  Award, 
  Shield, 
  Sparkles,
  ArrowRight,
  CheckCircle,
  Quote
} from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import hospitalImage from "@assets/IMG_1816_1765793586294.webp";

const testimonials = [
  {
    id: 1,
    name: "James Thompson",
    location: "London, UK",
    text: "The entire experience exceeded my expectations. From the initial consultation to the follow-up care, the team at Hairtrans Turkey was professional and caring.",
    rating: 5,
  },
  {
    id: 2,
    name: "Michael Schmidt",
    location: "Berlin, Germany",
    text: "I researched many clinics before choosing Hairtrans Turkey. The results are incredible - natural-looking and exactly what I hoped for.",
    rating: 5,
  },
  {
    id: 3,
    name: "Ahmed Al-Rashid",
    location: "Dubai, UAE",
    text: "World-class facility with expert surgeons. The VIP treatment and attention to detail made my hair transplant journey seamless.",
    rating: 5,
  },
];

export default function Home() {
  const { t } = useTranslation();

  const stats = [
    { value: "10,000+", label: t("home.stats.procedures"), icon: Users },
    { value: "15+", label: t("home.stats.experience"), icon: Award },
    { value: "98%", label: t("home.stats.satisfaction"), icon: Star },
    { value: "ISO", label: t("home.stats.certified"), icon: Shield },
  ];

  const procedures = [
    {
      id: "fue",
      title: t("home.procedures.fue.title"),
      description: t("home.procedures.fue.description"),
      features: [
        t("home.procedures.fue.feature1"),
        t("home.procedures.fue.feature2"),
        t("home.procedures.fue.feature3"),
      ],
      icon: Sparkles,
    },
    {
      id: "dhi",
      title: t("home.procedures.dhi.title"),
      description: t("home.procedures.dhi.description"),
      features: [
        t("home.procedures.dhi.feature1"),
        t("home.procedures.dhi.feature2"),
        t("home.procedures.dhi.feature3"),
      ],
      icon: Sparkles,
    },
    {
      id: "sapphire",
      title: t("home.procedures.sapphire.title"),
      description: t("home.procedures.sapphire.description"),
      features: [
        t("home.procedures.sapphire.feature1"),
        t("home.procedures.sapphire.feature2"),
        t("home.procedures.sapphire.feature3"),
      ],
      icon: Sparkles,
    },
  ];

  const benefits = [
    t("home.whyUs.benefit1"),
    t("home.whyUs.benefit2"),
    t("home.whyUs.benefit3"),
    t("home.whyUs.benefit4"),
    t("home.whyUs.benefit5"),
    t("home.whyUs.benefit6"),
  ];

  return (
    <div className="min-h-screen">
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="section-hero">
        <img
          src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=2000&q=80"
          alt=""
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <Badge variant="secondary" className="mb-6 bg-white/10 text-white border-white/20 backdrop-blur-sm">
            {t("home.hero.badge")}
          </Badge>
          
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight" data-testid="text-hero-title">
            {t("home.hero.title1")}<br />
            <span className="text-primary">{t("home.hero.title2")}</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed">
            {t("home.hero.subtitle")}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/contact">
              <Button size="lg" className="text-base px-8 py-6" data-testid="button-hero-consultation">
                {t("home.hero.getConsultation")}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link href="/gallery">
              <Button size="lg" variant="outline" className="text-base px-8 py-6 bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-testid="button-hero-gallery">
                {t("home.hero.viewResults")}
              </Button>
            </Link>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </section>

      <section className="py-12 bg-card border-y border-border" data-testid="section-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24" data-testid="section-procedures">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">{t("home.procedures.badge")}</Badge>
            <h2 className="font-serif text-3xl lg:text-5xl font-bold text-foreground mb-4" data-testid="text-procedures-title">
              {t("home.procedures.title")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("home.procedures.subtitle")}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {procedures.map((procedure) => (
              <Card key={procedure.id} className="group hover-elevate" data-testid={`card-procedure-${procedure.id}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <procedure.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-foreground mb-3">
                    {procedure.title}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {procedure.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {procedure.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link href={`/services#${procedure.id}`}>
                    <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors">
                      {t("home.procedures.learnMore")} — {procedure.title}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-card" data-testid="section-why-turkey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="secondary" className="mb-4">{t("home.whyUs.badge")}</Badge>
              <h2 className="font-serif text-3xl lg:text-5xl font-bold text-foreground mb-6" data-testid="text-why-title">
                {t("home.whyUs.title")}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t("home.whyUs.subtitle")}
              </p>
              
              <ul className="space-y-4 mb-8">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              
              <Link href="/about">
                <Button data-testid="button-learn-more">
                  {t("home.whyUs.learnMore")}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src={hospitalImage}
                  alt="Liv Hospital Istanbul - Hairtrans Turkey Clinic"
                  className="w-full h-full object-cover"
                  width={800}
                  height={535}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-background p-6 rounded-xl shadow-lg border border-border">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold text-foreground">{t("home.whyUs.accredited")}</div>
                    <div className="text-sm text-muted-foreground">{t("home.whyUs.standards")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24" data-testid="section-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">{t("home.testimonials.badge")}</Badge>
            <h2 className="font-serif text-3xl lg:text-5xl font-bold text-foreground mb-4" data-testid="text-testimonials-title">
              {t("home.testimonials.title")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("home.testimonials.subtitle")}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="relative" data-testid={`card-testimonial-${testimonial.id}`}>
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-primary/20 mb-4" />
                  <p className="text-foreground mb-6 leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.location}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-primary" data-testid="section-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-5xl font-bold text-primary-foreground mb-6" data-testid="text-cta-title">
            {t("home.cta.title")}
          </h2>
          <p className="text-lg text-primary-foreground/90 mb-10 max-w-2xl mx-auto">
            {t("home.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/contact">
              <Button size="lg" variant="secondary" className="text-base px-8 py-6" data-testid="button-cta-consultation">
                {t("home.cta.getConsultation")}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="https://wa.me/905551234567" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="text-base px-8 py-6 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10" data-testid="button-cta-whatsapp">
                {t("home.cta.whatsapp")}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
